// packages/web-ui/src/hooks/useDropZone.ts
//
// Story W2-S7 AC #9 — file drop-zone hook. Wires `onDragOver` /
// `onDragEnter` / `onDragLeave` / `onDrop` to the host element and
// surfaces a `dragActive` flag for visual feedback. Files are routed via
// the `onFileDropped` callback supplied by the parent (W2-S7 dispatches
// into the W2-S6 `<FileUploadConfirm>` card).
//
// Owned by W2-S7; consumed by W2-S8 (no — W2-S8 has its own drop), and
// any future "drop a file anywhere on the panel" surface.

import { useCallback, useState } from 'react';
import type { DragEvent } from 'react';

export interface UseDropZoneOptions {
  onFileDropped: (file: File) => void;
  /** Optional gate — when false, drop events are ignored. Default true. */
  enabled?: boolean;
}

export interface UseDropZoneResult {
  dragActive: boolean;
  /** Handlers to spread onto the host element. */
  handlers: {
    onDragOver: (event: DragEvent<HTMLElement>) => void;
    onDragEnter: (event: DragEvent<HTMLElement>) => void;
    onDragLeave: (event: DragEvent<HTMLElement>) => void;
    onDrop: (event: DragEvent<HTMLElement>) => void;
  };
}

/**
 * Returns drag-active state + a bundle of event handlers for any element
 * that should accept file drops.
 *
 * Behaviour:
 *   - `dragOver` / `dragEnter` flip `dragActive` to `true` and call
 *     `preventDefault` so the browser allows the drop.
 *   - `dragLeave` flips back to `false`.
 *   - `drop` reads `event.dataTransfer.files`. For each `File`, the
 *     consumer's `onFileDropped` is called. Non-file drops (e.g. text
 *     drag) are ignored — the `files` list is empty in that case.
 */
export function useDropZone(options: UseDropZoneOptions): UseDropZoneResult {
  const { onFileDropped, enabled = true } = options;
  const [dragActive, setDragActive] = useState(false);

  const onDragOver = useCallback(
    (event: DragEvent<HTMLElement>): void => {
      if (!enabled) return;
      event.preventDefault();
    },
    [enabled],
  );

  const onDragEnter = useCallback(
    (event: DragEvent<HTMLElement>): void => {
      if (!enabled) return;
      // Only flip the flag when the drag carries files (avoid flicker on
      // text/anchor drags).
      if (event.dataTransfer?.types.includes('Files') === true) {
        setDragActive(true);
      }
      event.preventDefault();
    },
    [enabled],
  );

  const onDragLeave = useCallback(
    (event: DragEvent<HTMLElement>): void => {
      if (!enabled) return;
      // Use relatedTarget to ignore moves between child elements.
      const related = event.relatedTarget;
      if (related instanceof Node && event.currentTarget.contains(related)) return;
      setDragActive(false);
    },
    [enabled],
  );

  const onDrop = useCallback(
    (event: DragEvent<HTMLElement>): void => {
      if (!enabled) return;
      event.preventDefault();
      setDragActive(false);
      const dt = event.dataTransfer;
      if (dt === null || dt === undefined) return;
      const files = Array.from(dt.files ?? []);
      for (const file of files) {
        onFileDropped(file);
      }
    },
    [enabled, onFileDropped],
  );

  return {
    dragActive,
    handlers: { onDragOver, onDragEnter, onDragLeave, onDrop },
  };
}
